import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Delete, Edit, RestoreFromTrash } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import IconButton from '@material-ui/core/IconButton';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import { deactivateUser, UserListDto } from 'api/user';
import { Order } from 'utils/sort';
import TableSortCell from 'components/atoms/TablePagination/TableSortCell';
import SearchBar from 'components/atoms/SearchBar/SearchBar';
import Spin from 'components/atoms/Spin/Spin';
import { useDictionaryApi } from 'api/dictionary';
import { ConfirmContext } from '../../components/atoms/ConfirmProvider/ConfirmProvider';

const Users = () => {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const confirm = useContext(ConfirmContext);
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<UserListDto[]>([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof UserListDto>('id');
    const [search, setSearch] = React.useState<string | null>(null);
    const { getPaged } = useDictionaryApi<UserListDto>('/users');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPaged(page, perPage, `${orderBy},${order}`, search);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [getPaged, page, perPage, orderBy, order, search, enqueueSnackbar]);

    const handleRequestSort = (property: keyof UserListDto) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRemove = (userId: number | undefined, status: string | undefined) => {
        confirm({}).then(async () => {
            setLoading(true);
            try {
                await deactivateUser(userId, status === 'ACTIVE' ? 'DELETED' : 'ACTIVE');
                await fetch();
            } catch {
                enqueueSnackbar('Ошибка при удалении', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <Box position={'relative'}>
            <SearchBar
                buttonText="Добавить"
                placeholder="Введите текст"
                onButtonClick={() => {
                    history.push('/cabinet/user-edit');
                }}
                onSearchChange={(event: any) => {
                    setSearch(event.target.value);
                }}
            ></SearchBar>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'small'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableSortCell
                                    label={'№'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'id'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Фамилия</TableCell>
                                <TableCell>Имя</TableCell>
                                <TableCell>Отчество</TableCell>
                                <TableCell>Телефон</TableCell>
                                <TableCell>Почта</TableCell>
                                <TableCell>Регион</TableCell>
                                <TableCell>Роль</TableCell>
                                <TableCell>Действии</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index} hover={true}>
                                    <TableCell>{page * perPage + index + 1}</TableCell>
                                    <TableCell>{item.last_name}</TableCell>
                                    <TableCell>{item.first_name}</TableCell>
                                    <TableCell>{item.middle_name}</TableCell>
                                    <TableCell>{item.phone_number}</TableCell>
                                    <TableCell>{item.email}</TableCell>
                                    <TableCell>{item.region?.description}</TableCell>
                                    <TableCell>
                                        {item.roles?.flatMap((r) => r.description).join(', ')}
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/cabinet/user-edit/${item.id}`}>
                                            <IconButton>
                                                <Edit />
                                            </IconButton>
                                        </Link>
                                        <IconButton
                                            onClick={() => handleRemove(item.id, item.status)}
                                        >
                                            {item.status === 'DELETED' ? (
                                                <RestoreFromTrash />
                                            ) : (
                                                <Delete />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    total={total}
                    perPage={perPage}
                    page={page}
                    onChangePage={setPage}
                    onChangeRowsPerPage={setPerPage}
                />
            </Box>
        </Box>
    );
};

export default Users;
